import { normalizeHexColor } from '../lib/levelEditorColors';
import type {
  ColorCellReference,
  EditorLayer,
  LayerCell,
  TileBrush,
  TileReference,
  UploadedImageTransformSnapshot,
} from '../types/levelEditor';

import { cloneTileReference } from './editorStoreCloning';

export const makeTileKey = (x: number, y: number): string => `${x},${y}`;

export const parseTileKey = (key: string): { x: number; y: number } => {
  const parts = key.split(',');
  if (parts.length !== 2) {
    throw new TypeError(`Invalid tile key "${key}": expected "x,y" format`);
  }
  const x = Number(parts[0].trim());
  const y = Number(parts[1].trim());
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    throw new TypeError(`Invalid tile key "${key}": coordinates must be finite numbers`);
  }
  return { x, y };
};

export const createColorCell = (color: string): ColorCellReference => ({
  kind: 'color',
  color: normalizeHexColor(color),
});

export const isColorCell = (
  value: LayerCell | TileReference | null | undefined,
): value is ColorCellReference => value?.kind === 'color';

export const cloneLayerCell = (cell: LayerCell): LayerCell =>
  isColorCell(cell) ? { kind: 'color', color: cell.color } : cloneTileReference(cell);

const cloneUploadedImageTransform = (
  transform: UploadedImageTransformSnapshot,
): UploadedImageTransformSnapshot => ({ ...transform });

export const cloneLayers = (layers: EditorLayer[]): EditorLayer[] =>
  layers.map((layer) => ({
    ...layer,
    tiles: { ...layer.tiles },
    ...(layer.uploadedImageTransform
      ? { uploadedImageTransform: cloneUploadedImageTransform(layer.uploadedImageTransform) }
      : {}),
  }));

export const isAnimatedGifLayer = (layer: EditorLayer): boolean =>
  layer.layerType === 'animated-gif';

export const normalizeLayerName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'Layer';
  }
  return trimmed.slice(0, 28);
};

export const normalizeLayerLookupName = (value: string): string => value.trim().toLowerCase();

export const isSameTile = (left: LayerCell | null, right: LayerCell | null): boolean => {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  if (isColorCell(left) || isColorCell(right)) {
    return isColorCell(left) && isColorCell(right) && left.color === right.color;
  }
  return (
    left.tilesetId === right.tilesetId &&
    left.sheetId === right.sheetId &&
    left.imagePath === right.imagePath &&
    left.sourceX === right.sourceX &&
    left.sourceY === right.sourceY &&
    left.tileSize === right.tileSize
  );
};

export const tryCreateBrushFromTile = (tile: LayerCell | undefined): TileBrush | null => {
  if (!tile || isColorCell(tile)) {
    return null;
  }
  return {
    width: 1,
    height: 1,
    tiles: [{ offsetX: 0, offsetY: 0, tile: cloneTileReference(tile) }],
  };
};
